namespace Solovis.Reports.Parameters.Stats {

    export class SequenceEditorControl extends Solovis.UI.BaseControl {
      
        private _sequence: SequenceOptions;
        private _initialWidth;

        private _formBuilder: Solovis.UI.InputControls.FormBuilder;

        constructor(options) {
            super(options);

            this._sequence = options.sequence ? options.sequence : {};            
        }

        public doRender() {
            var formBuilderDiv = $("<div>").appendTo(this.container);
            this._formBuilder = new Solovis.UI.InputControls.FormBuilder({
                container: formBuilderDiv,
                labelColumnWidth: 245
            });

            this._formBuilder.addEntry({
                name: "label",
                label: "Label",

                initialValue: this._sequence.label,

                editor: {
                    type: "text"
                }
            });

            this._formBuilder.addEntry({
                name: "code",
                label: "Code",

                initialValue: this._sequence.code,

                editor: {
                    type: "text"
                }
            });

            this._formBuilder.addEntry({
                name: "type",
                label: "Type",

                initialValue: this._sequence.type ? this._sequence.type : "Month",

                editor: {
                    type: "select",
                    selectOptions: {
                        data: [
                            { text: "Month", value: "Month" },
                            { text: "Calendar Quarter", value: "CalendarQuarter" },
                            { text: "Floating Quarter", value: "FloatingQuarter" },
                            { text: "Calendar Year", value: "CalendarYear" }
                        ]
                    }
                }
            });

            // Performance Variation
            let customEnvironmentUserData = this._formBuilder.portalServices().customEnvironmentUserData();

            // Determine TWR variations list.
            let perfVariationsOptionsList = [
                { text: "", value: "" }
            ];
            if (customEnvironmentUserData && customEnvironmentUserData.ModelSettings) {
                let modelSettings = customEnvironmentUserData.ModelSettings;
                if (modelSettings && modelSettings.performanceVariationsConfig && modelSettings.performanceVariationsConfig.variations) {
                    for (let variation of modelSettings.performanceVariationsConfig.variations) {
                        // chainLinkBy variations are not supported with sequences.
                        if (!variation.chainLinkBy) {
                            perfVariationsOptionsList.push({ text: variation.label, value: variation.code });
                        }
                    }
                }
            }

            this._formBuilder.addEntry({
                name: "perfVariationCode",
                label: "Performance Variation",

                initialValue: this._sequence.perfVariationCode ? this._sequence.perfVariationCode : "",

                editor: {
                    type: "select",
                    selectOptions: {
                        data: perfVariationsOptionsList
                    }
                }
            });

            let annualizationOptions = Solovis.Reports.Parameters.AnnualizationMethodParameterControl.getAnnualizationOptions();
            annualizationOptions.splice(0, 0, { text: "Default", value: "" });

            this._formBuilder.addEntry({
                name: "annualizationMethod",
                label: "Annualization Method",

                initialValue: this._sequence.annualizationMethod ? this._sequence.annualizationMethod : "",

                editor: {
                    type: "select",
                    selectOptions: {
                        data: annualizationOptions
                    }
                }
            });

            // Basic Stats
            this._formBuilder.addEntry({
                isGroupHeader: true,
                name: "basicStats",
                label: "Basic Stats"
            });

            let basicStats = this._sequence.basicStats ? this._sequence.basicStats : {};

            for (let statsField of StatSetEditorDialog._sequenceBasicStatsFieldList) {
                let label = StatSetEditorDialog._sequenceStatsFieldLabels[statsField];
                if (!label) {
                    label = statsField;
                }

                this._formBuilder.addEntry({
                    name: statsField,
                    label: label,

                    help: StatSetEditorDialog._statsFieldHelp[statsField],

                    initialValue: basicStats[statsField],

                    editor: {
                        type: "bool"
                    }
                });
            }

            this._formBuilder.addEntry({
                name: "gainLossSummary",
                label: "Gain/Loss Summary",

                help: { url: "~/Help/InvestmentModelHelp/CalculationsReference#monthly_gain_loss_summary" },

                initialValue: this._sequence.gainLossSummary,

                editor: {
                    type: "bool"
                }
            });

            // Benchmarked Basic Stats
            this._formBuilder.addEntry({
                isGroupHeader: true,
                name: "benchmarkedBasicStats",
                label: "Benchmarked Basic Stats"
            });

            let benchmarkedBasicStats: StatSetOptionsBenchmarkedBasicStats = this._sequence.benchmarkedBasicStats ? this._sequence.benchmarkedBasicStats : {};

            for (let statsField of StatSetEditorDialog._benchmarkedStatsFieldList) {
                let label = StatSetEditorDialog._sequenceStatsFieldLabels[statsField];
                if (!label) {
                    label = statsField;
                }

                this._formBuilder.addEntry({
                    name: statsField,
                    label: label,

                    help: StatSetEditorDialog._statsFieldHelp[statsField],

                    initialValue: benchmarkedBasicStats[statsField],

                    editor: {
                        type: "bool"
                    }
                });
            }

            this._addBetaAlpha();
            this._addInformationRatio();

            this._formBuilder.setupDone();

            if (this._initialWidth) {
                this._formBuilder.setWidth(this._initialWidth);
            }
        }

        private _addBetaAlpha() {
            let sequence = this._sequence;

            let formBuilder = this._formBuilder;

            formBuilder.addEntry({
                isGroupHeader: true,
                name: "betaAlphaGroup",
                label: "Beta/Alpha"
            });

            formBuilder.addEntry({
                name: "betaAlpha",
                label: "Beta/Alpha",

                help: { url: "~/Help/InvestmentModelHelp/CalculationsReference#beta_alpha" },

                initialValue: !!sequence.betaAlpha,

                editor: {
                    type: "bool"
                }
            });

            this._addBenchmarkRefEntry(
                "betaAlpha.marketRef", "Beta/Alpha Market Benchmark", sequence.betaAlpha ? sequence.betaAlpha.marketRef : null,
                {
                    noRiskFreeRate: true,
                    noFixedRate: true,
                    isVisibleParent: "betaAlpha"
                });

            formBuilder.addEntry({
                name: "betaAlpha.annualizedAlpha",
                label: "Annualized Alpha",

                help: { url: "~/Help/InvestmentModelHelp/CalculationsReference#annualized_alpha" },

                initialValue: sequence.betaAlpha ? !!sequence.betaAlpha.annualizedAlpha : false,

                editor: {
                    type: "bool"
                },

                isVisible: (entry) => { return !!this._formBuilder.getValueByName("betaAlpha"); }
            });

            formBuilder.addEntry({
                name: "betaAlpha.annualizedTreynorRatio",
                label: "Annualized Treynor Ratio",

                help: { url: "~/Help/InvestmentModelHelp/CalculationsReference#annualized_treynor_ratio" },

                initialValue: sequence.betaAlpha ? !!sequence.betaAlpha.annualizedTreynorRatio : false,

                editor: {
                    type: "bool"
                },

                isVisible: (entry) => { return !!this._formBuilder.getValueByName("betaAlpha"); }
            });
        }

        private _addInformationRatio() {
            let sequence = this._sequence;

            let formBuilder = this._formBuilder;

            formBuilder.addEntry({
                isGroupHeader: true,
                name: "informationRatioGroup",
                label: "Information Ratio"
            });

            formBuilder.addEntry({
                name: "informationRatio",
                label: "Information Ratio",

                help: { url: "~/Help/InvestmentModelHelp/CalculationsReference#information_ratio" },

                initialValue: !!sequence.informationRatio,

                editor: {
                    type: "bool"
                }
            });

            this._addBenchmarkRefEntry(
                "informationRatio.benchmarkRef", "Information Ratio Benchmark", sequence.informationRatio ? sequence.informationRatio.benchmarkRef : null,
                {
                    isVisibleParent: "informationRatio"
                });
        }

        private _addBenchmarkRefEntry(
            name,
            label,
            initialValue,
            benchmarkOptions?: {
                isRiskFreeRate?: boolean,
                noRiskFreeRate?: boolean,
                noFixedRate?: boolean,
                isVisibleParent?: string
            }
        ) {
            this._formBuilder.addEntry({
                name: name,
                label: label,

                initialValue: initialValue,

                createEditor: (createEditorOptions) => {
                    let benchmarkControl = new StatSetBenchmarkRefControl({
                        controlHost: createEditorOptions.container,
                        benchmarkRef: createEditorOptions.initialValue,
                        isRiskFreeRate: benchmarkOptions && benchmarkOptions.isRiskFreeRate,
                        noRiskFreeRate: benchmarkOptions && benchmarkOptions.noRiskFreeRate,
                        noFixedRate: benchmarkOptions && benchmarkOptions.noFixedRate
                    });
                    benchmarkControl.setAsCurrentControl();

                    let editor = {
                        getValue: () => {
                            return benchmarkControl.getBenchmarkRef();
                        }
                    };

                    return editor;
                },

                isVisible: (entry) => {
                    if (!benchmarkOptions || !benchmarkOptions.isVisibleParent) {
                        return true;
                    }
                    else if (entry.formBuilder.getValueByName(benchmarkOptions.isVisibleParent)) {
                        return true;
                    }
                    else {
                        return false;
                    }
                }
            });
        }

        public getSequence() {
            if (this._formBuilder) {
                let label = this._formBuilder.getValueByName("label");
                let code = this._formBuilder.getValueByName("code");
                let type = this._formBuilder.getValueByName("type");
                let perfVariationCode = this._formBuilder.getValueByName("perfVariationCode");
                let annualizationMethod = this._formBuilder.getValueByName("annualizationMethod");

                let newSequence: SequenceOptions = {};                   

                if (label) {
                    newSequence.label = label;
                }

                if (code) {
                    newSequence.code = code;
                }                

                if (type) {
                    newSequence.type = type;
                } 

                if (perfVariationCode) {
                    newSequence.perfVariationCode = perfVariationCode;
                }
               
                if (annualizationMethod) {
                    newSequence.annualizationMethod = annualizationMethod;
                }

                // basicStats
                newSequence.basicStats = {};
                for (let statsField of StatSetEditorDialog._sequenceBasicStatsFieldList) {
                    let isStatEnabled = this._formBuilder.getValueByName(statsField);
                    if (isStatEnabled) {
                        newSequence.basicStats[statsField] = true;
                    }
                }

                // benchmarkedBasicStats
                let benchmarkedBasicStats: StatSetOptionsBenchmarkedBasicStats = {};
                for (let statsField of StatSetEditorDialog._benchmarkedStatsFieldList) {
                    let isStatEnabled = this._formBuilder.getValueByName(statsField);
                    if (isStatEnabled) {
                        benchmarkedBasicStats[statsField] = true;
                    }
                }

                newSequence.benchmarkedBasicStats = benchmarkedBasicStats;

                // gainLossSummary
                let gainLossSummary = this._formBuilder.getValueByName("gainLossSummary");
                if (gainLossSummary) {
                    newSequence.gainLossSummary = true;
                }

                // beta/alpha
                this._appyBetaAlphaToNewSequenceOptions(newSequence);

                // informationRatio
                this._applyInformationRatioToNewSequenceOptions(newSequence);

                return newSequence;
            }
            else {
                return this._sequence;
            }
        }

        private _appyBetaAlphaToNewSequenceOptions(newOptions: SequenceOptions) {
            let betaAlpha = this._formBuilder.getValueByName("betaAlpha");
            if (betaAlpha) {
                let betaAlphaOptions: StatSetOptionsBetaAlpha = {};
                betaAlphaOptions.marketRef = this._formBuilder.getValueByName("betaAlpha.marketRef");
                if (this._formBuilder.getValueByName("betaAlpha.annualizedAlpha")) {
                    betaAlphaOptions.annualizedAlpha = true;
                }
                if (this._formBuilder.getValueByName("betaAlpha.annualizedTreynorRatio")) {
                    betaAlphaOptions.annualizedTreynorRatio = true;
                }

                newOptions.betaAlpha = betaAlphaOptions;
            }
        }

        private _applyInformationRatioToNewSequenceOptions(newOptions: SequenceOptions) {
            let informationRatio = this._formBuilder.getValueByName("informationRatio");
            if (informationRatio) {
                let informationRatioOptions: StatSetOptionsInformationRatio = {};
                informationRatioOptions.benchmarkRef = this._formBuilder.getValueByName("informationRatio.benchmarkRef");

                newOptions.informationRatio = informationRatioOptions;
            }
        }

        public setWidth(newWidth) {
            if (this._formBuilder) {
                this._formBuilder.setWidth(newWidth);
            }
            else {
                this._initialWidth = newWidth;
            }
        }
    }

}